import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields';
import dayjs from 'dayjs';
import { IMaintenance } from 'features/inventory/maintenance/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { maintenanceService } from 'shared/services';
import { StatusOption } from 'shared/types/general';

const CLOSURE_STATUS_VALUES = [2, 3, 4];

interface CloseMaintenanceFormProps {
  onSubmit: (data: any) => void;
  maintenance?: IMaintenance | null;
}

export const CloseMaintenanceForm = ({ onSubmit, maintenance }: CloseMaintenanceFormProps) => {
  const { t } = useTranslation();

  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);

  const translateStatus = (display_name: string) =>
    t(`pages.maintenance.status_options.${display_name}`, {
      defaultValue: display_name
    });

  useEffect(() => {
    maintenanceService.options().then((res) => {
      if (res.actions && res.actions.POST) {
        const choices = res.actions.POST.status.choices as StatusOption[];
        const closureOptions = choices
          .filter((item) => CLOSURE_STATUS_VALUES.includes(item.value))
          .map((item) => ({
            ...item,
            display_name: translateStatus(item.display_name)
          }));

        const currentValue = maintenance?.status?.value;
        const currentInClosure =
          currentValue != null && CLOSURE_STATUS_VALUES.includes(currentValue);

        if (currentValue != null && !currentInClosure && maintenance?.status) {
          const currentOption = choices.find((item) => item.value === currentValue);
          if (currentOption) {
            closureOptions.unshift({
              ...currentOption,
              display_name: translateStatus(currentOption.display_name)
            });
          } else {
            closureOptions.unshift({
              value: maintenance.status.value,
              display_name: translateStatus(maintenance.status.display_name)
            });
          }
        }

        setStatusOptions(closureOptions);
      }
    });
  }, [maintenance?.status?.value, maintenance?.status?.display_name, t]);

  const statusInitValue = useMemo(() => {
    if (!maintenance?.status) {
      return undefined;
    }
    return {
      ...maintenance.status,
      display_name: translateStatus(maintenance.status.display_name)
    };
  }, [maintenance?.status, t]);

  return (
    <EnhancedForm
      onSubmit={onSubmit}
      formId="closeMaintenance"
      hideControls
      autoStyleLayout
      columns={2}>
      <IdentityField
        name="execution_date"
        component={DatePickerAdapter}
        label={t(`global.execution_date`)}
        initialValue={dayjs().format('YYYY-MM-DD')}
        type="text"
        required
      />
      <IdentityField
        name="status"
        label={t(`global.status`)}
        component={AutocompleteAdapter}
        options={statusOptions}
        initValue={statusInitValue}
        type="text"
        optionLabel="display_name"
        optionValue="value"
        required
      />
      <IdentityField
        gridSpan={2}
        name="notes"
        label={t(`global.notes`)}
        component={TextFieldAdapter}
        initialValue={maintenance?.notes ?? ''}
        type="text"
        inputProps={{
          multiline: true,
          minRows: 2
        }}
      />
    </EnhancedForm>
  );
};

export default CloseMaintenanceForm;
